/**
 * An Analytics panel draws its data in one of several shapes, picked by `config.display.type`.
 * This file exports the list of those shapes and everything declarative about each one: its
 * name in the UI, which config block it reads, which knowledge guide documents it, and the
 * size it wants when it is created. Both the API and the Cirra frontend read this list, so a
 * new panel type is declared once here instead of being spelled out in every place that
 * branches on the type string.
 *
 * The React parts of a type (its icon, its panel body, its editor) cannot live here because
 * this file is also loaded by the server. They live in the frontend's own registry, keyed by
 * the same ids.
 */

/*
 * Per-entry fields:
 *
 * - `id`         the value stored in `config.display.type`.
 * - `label`      the type's name in the UI, sentence case.
 * - `phrase`     the same name inside a sentence ("shown as a bar chart"). Absent when no
 *                surface says it that way yet.
 * - `block`      the key under `config.display` holding this type's settings. Several types
 *                share one block when they need the same settings (bar and line, or the three
 *                value-against-target names). A table's settings are its columns, which any
 *                type may also carry, so `columns` is a block here without being table-only.
 * - `guide`      the file under `immutable/analytics/panel-types/` teaching the agent when to
 *                choose this type. Filenames do not always match the id.
 * - `container`  true when the type holds other panels instead of data.
 * - `advanced`   true when display code may return this type. HTML is excluded because its
 *                markup is compiled and validated when the panel cooks; containers are
 *                excluded because they hold panels rather than a display.
 * - `pinnedLayout`   a size the type keeps forever, re-applied on every write, client and
 *                    server alike.
 * - `creationLayout` a size written onto the stored layout once, when the type is picked. The
 *                    panel stays freely resizable afterwards.
 *
 * The order of this list is the order every generated list uses: the display-type picker, the
 * tool schema's enum, and the guide index in the system prompt.
 */
export const analyticsPanelTypes = [
  { id: 'table', label: 'Table', phrase: 'a table', block: 'columns', guide: 'table.md', advanced: true },
  { id: 'bar', label: 'Bar chart', phrase: 'a bar chart', block: 'chart', guide: 'bar-chart.md', advanced: true },
  { id: 'line', label: 'Line chart', phrase: 'a line chart', block: 'chart', guide: 'line-chart.md', advanced: true },
  { id: 'pie', label: 'Pie chart', phrase: 'a pie chart', block: 'pie', guide: 'pie.md', advanced: true },
  { id: 'treemap', label: 'Treemap', phrase: 'a treemap', block: 'treemap', guide: 'treemap.md', advanced: true },
  { id: 'funnel', label: 'Funnel', phrase: 'a funnel', block: 'funnel', guide: 'funnel.md', advanced: true },
  { id: 'stat', label: 'Stat', phrase: 'a stat tile', block: 'stat', guide: 'stat.md', advanced: true, pinnedLayout: { w: 3, h: 3 } },
  { id: 'progress', label: 'Progress', phrase: 'a progress bar', block: 'target', guide: 'progress.md', advanced: true, creationLayout: { w: 3, h: 4 } },
  { id: 'gauge', label: 'Gauge', phrase: 'a gauge', block: 'target', guide: 'gauge.md', advanced: true, creationLayout: { w: 4, h: 5 } },
  /* No creation size: a bullet's height follows how many rows the query returns, which is not
     known when the type is picked. */
  { id: 'bullet', label: 'Bullet', phrase: 'bullet tracks', block: 'target', guide: 'bullet.md', advanced: true },
  { id: 'html', label: 'HTML', phrase: 'a custom HTML panel', block: 'html', guide: 'html.md', advanced: false },
  { id: 'map', label: 'Map', block: 'map', guide: 'map.md', advanced: true },
  { id: 'tabs', label: 'Tabs', block: 'tabs', guide: 'tabs.md', container: true, advanced: false },
  { id: 'group', label: 'Group', block: 'group', guide: 'group.md', container: true, advanced: false }
];

/* Names stored before a rename. They still arrive from saved panels, so every surface reading a
   stored type has to recognize them, but they are never offered as a choice. */
const ALIASES = { list: 'table' };

const typesById = new Map(analyticsPanelTypes.map((type) => [type.id, type]));

/* Resolves a stored type, alias included, to its entry. Unknown types return null so the caller
   decides what an unrecognized panel does. */
export const getAnalyticsPanelType = (id) => typesById.get(ALIASES[id] || id) || null;

export const ANALYTICS_PANEL_TYPE_IDS = analyticsPanelTypes.map((type) => type.id);

/* Every type display code may return, plus the aliases, since a stored alias is still a valid
   thing to return. This is what advanced-mode validation accepts. */
export const ANALYTICS_ADVANCED_DISPLAY_TYPE_IDS = [...analyticsPanelTypes.filter((type) => type.advanced).map((type) => type.id), ...Object.keys(ALIASES)];

export const ANALYTICS_PANEL_GUIDE_FILENAMES = analyticsPanelTypes.map((type) => type.guide);

/* Every key under `display` that holds a type's settings, in type order. This is what a surface
   walks when it has to describe or echo back a panel's whole configuration, instead of keeping its
   own list of the blocks it happens to know about. */
export const ANALYTICS_DISPLAY_BLOCK_NAMES = [...new Set(analyticsPanelTypes.map((type) => type.block).filter(Boolean))];
